/**
 * Methodology Assessment Page
 * 
 * Standalone page for assessing finished methodologies against Terms of Reference.
 * Users can upload ToR, extract requirements, and compare methodology text segments.
 */

import React, { useState, useCallback } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Alert,
    Grid,
    TextField,
    CircularProgress,
    Stack,
    Divider
} from '@mui/material';
import {
    CloudUpload as CloudUploadIcon,
    AutoFixHigh as AutoFixHighIcon,
    Description as DescriptionIcon,
    Assessment as AssessmentIcon
} from '@mui/icons-material';
import MethodologyAssessmentResults from '../components/MethodologyAssessmentResults';
import ExtractedRequirementsSummary from '../components/ExtractedRequirementsSummary';
import {
    TorExtractionResult,
    MethodologyAssessmentResult,
    uploadTor,
    assessMethodology,
    isMethodologyBuilderConfigured
} from '../services/methodology';
import * as pdfjsLib from 'pdfjs-dist';

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

const MethodologyAssessmentPage: React.FC = () => {
    // ToR state
    const [torFile, setTorFile] = useState<File | null>(null);
    const [torExtraction, setTorExtraction] = useState<TorExtractionResult | null>(null);
    const [torText, setTorText] = useState<string>('');
    const [isExtracting, setIsExtracting] = useState(false);
    
    // Assessment inputs
    const [torSegmentText, setTorSegmentText] = useState<string>('');
    const [methodologyText, setMethodologyText] = useState<string>('');
    
    // Assessment results
    const [assessmentResult, setAssessmentResult] = useState<MethodologyAssessmentResult | null>(null);
    const [isAssessing, setIsAssessing] = useState(false);
    
    // Errors
    const [error, setError] = useState<string | null>(null);
    
    // Check configuration
    const isConfigured = isMethodologyBuilderConfigured();
    
    /**
     * Extract text from PDF
     */
    const extractTextFromPDF = async (file: File): Promise<string> => {
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        const textPages: string[] = [];
        
        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
            const page = await pdf.getPage(pageNum);
            const textContent = await page.getTextContent();
            
            let lastY = -1;
            let pageText = '';
            
            for (const item of textContent.items) {
                const textItem = item as any;
                
                if (lastY !== -1 && Math.abs(textItem.transform[5] - lastY) > 5) {
                    pageText += '\n';
                }
                
                if (pageText.length > 0 && !pageText.endsWith('\n') && !pageText.endsWith(' ')) {
                    pageText += ' ';
                }
                
                pageText += textItem.str;
                lastY = textItem.transform[5];
            }
            
            textPages.push(pageText);
        }
        
        return textPages.join('\n\n');
    };
    
    /**
     * Handle ToR file upload
     */
    const handleTorFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        
        setTorFile(file);
        setError(null);
        setTorExtraction(null);
        setTorText('');
        
        try {
            let text: string;
            
            if (file.type === 'text/plain') {
                text = await new Promise<string>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result as string);
                    reader.onerror = reject;
                    reader.readAsText(file);
                });
            } else if (file.type === 'application/pdf') {
                text = await extractTextFromPDF(file);
            } else {
                throw new Error('Unsupported file type. Please upload PDF or TXT.');
            }
            
            setTorText(text);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to extract text from file');
        }
    }, []);
    
    /**
     * Extract ToR requirements
     */
    const handleExtractTor = useCallback(async () => {
        if (!torFile || !torText) {
            setError('Please upload a ToR file first.');
            return;
        }
        
        setIsExtracting(true);
        setError(null);
        setTorExtraction(null);
        
        try {
            const extraction = await uploadTor(torFile);
            setTorExtraction(extraction);
            
            // Pre-populate ToR segment with full text (user can edit)
            setTorSegmentText(torText.substring(0, 10000)); // First 10000 chars as default
        } catch (err) {
            setError(err instanceof Error ? err.message : 'ToR extraction failed');
        } finally {
            setIsExtracting(false);
        }
    }, [torFile, torText]);
    
    /**
     * Handle methodology file upload
     */
    const handleMethodologyFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        
        setError(null);
        
        try {
            let text: string;
            
            if (file.type === 'text/plain') {
                text = await new Promise<string>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(reader.result as string);
                    reader.onerror = reject;
                    reader.readAsText(file);
                });
            } else if (file.type === 'application/pdf') {
                text = await extractTextFromPDF(file);
            } else {
                throw new Error('Unsupported file type. Please upload PDF or TXT.');
            }
            
            setMethodologyText(text);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to extract text from file');
        }
    }, []);
    
    /**
     * Perform assessment
     */
    const handleAssess = useCallback(async () => {
        if (!torExtraction) {
            setError('Please extract ToR requirements first.');
            return;
        }
        
        if (!torSegmentText.trim() || !methodologyText.trim()) {
            setError('Please provide both ToR segment text and methodology text.');
            return;
        }
        
        setIsAssessing(true);
        setError(null);
        setAssessmentResult(null);
        
        try {
            // Pass both the extraction and the user's ToR segment text
            const result = await assessMethodology(methodologyText, torExtraction, torSegmentText);
            setAssessmentResult(result);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Assessment failed');
        } finally {
            setIsAssessing(false);
        }
    }, [torExtraction, torSegmentText, methodologyText]);
    
    /**
     * Reset all state
     */
    const handleReset = useCallback(() => {
        setTorFile(null);
        setTorText('');
        setTorExtraction(null);
        setTorSegmentText('');
        setMethodologyText('');
        setAssessmentResult(null);
        setError(null);
    }, []);
    
    if (!isConfigured) {
        return (
            <Box sx={{ p: 4 }}>
                <Alert severity="warning" sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        Methodology Assessment Not Configured
                    </Typography>
                    <Typography>
                        The Methodology Assessment service is not configured. Please contact your administrator
                        to set up the required environment variables.
                    </Typography>
                </Alert>
            </Box>
        );
    }
    
    return (
        <Box sx={{ p: 4, maxWidth: 1400, mx: 'auto' }}>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AssessmentIcon fontSize="large" />
                    Methodology Assessment
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Upload Terms of Reference, extract requirements, and assess your finished methodology for compliance.
                </Typography>
            </Box>
            
            {/* Error Display */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}
            
            <Grid container spacing={3}>
                {/* Step 1: Upload ToR */}
                <Grid item xs={12}>
                    <Paper elevation={2} sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CloudUploadIcon />
                            Step 1: Upload Terms of Reference
                        </Typography>
                        
                        <Box sx={{ mt: 2 }}>
                            <input
                                type="file"
                                accept=".pdf,.txt,application/pdf,text/plain"
                                onChange={handleTorFileSelect}
                                style={{ display: 'none' }}
                                id="tor-file-input"
                            />
                            <label htmlFor="tor-file-input">
                                <Button
                                    variant="contained"
                                    component="span"
                                    startIcon={<DescriptionIcon />}
                                    sx={{ mb: 2 }}
                                >
                                    {torFile ? 'Change ToR File' : 'Upload ToR (PDF/TXT)'}
                                </Button>
                            </label>
                            
                            {torFile && (
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Selected: {torFile.name} ({(torFile.size / 1024).toFixed(1)} KB)
                                    </Typography>
                                    {torText && (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                            Extracted {torText.length.toLocaleString()} characters
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        </Box>
                        
                        {torFile && torText && (
                            <Box sx={{ mt: 2 }}>
                                <Button
                                    variant="contained"
                                    onClick={handleExtractTor}
                                    disabled={isExtracting}
                                    startIcon={isExtracting ? <CircularProgress size={20} /> : <AutoFixHighIcon />}
                                >
                                    {isExtracting ? 'Extracting Requirements...' : 'Extract ToR Requirements'}
                                </Button>
                            </Box>
                        )}
                    </Paper>
                </Grid>
                
                {/* Step 2: Extracted Requirements Summary */}
                {torExtraction && (
                    <Grid item xs={12}>
                        <Paper elevation={2} sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                Extracted ToR Requirements
                            </Typography>
                            <ExtractedRequirementsSummary extraction={torExtraction} />
                        </Paper>
                    </Grid>
                )}
                
                {/* Step 3: Input Texts */}
                {torExtraction && (
                    <>
                        <Grid item xs={12} md={6}>
                            <Paper elevation={2} sx={{ p: 3 }}>
                                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <DescriptionIcon />
                                    ToR Segment Text
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Paste the specific ToR segment or requirements you want to compare against. 
                                    You can use the full ToR text or focus on a specific section.
                                </Typography>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={12}
                                    placeholder="Paste ToR segment or requirements text here..."
                                    value={torSegmentText}
                                    onChange={(e) => setTorSegmentText(e.target.value)}
                                />
                            </Paper>
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                            <Paper elevation={2} sx={{ p: 3 }}>
                                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <AutoFixHighIcon />
                                    Methodology Text
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Paste your finished methodology text to assess against the ToR requirements.
                                </Typography>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={12}
                                    placeholder="Paste methodology text here..."
                                    value={methodologyText}
                                    onChange={(e) => setMethodologyText(e.target.value)}
                                />
                                
                                <Box sx={{ mt: 2 }}>
                                    <input
                                        type="file"
                                        accept=".pdf,.txt,application/pdf,text/plain"
                                        onChange={handleMethodologyFileSelect}
                                        style={{ display: 'none' }}
                                        id="methodology-file-input"
                                    />
                                    <label htmlFor="methodology-file-input">
                                        <Button
                                            variant="outlined"
                                            component="span"
                                            startIcon={<CloudUploadIcon />}
                                            size="small"
                                        >
                                            Upload Methodology (PDF/TXT)
                                        </Button>
                                    </label>
                                </Box>
                            </Paper>
                        </Grid>
                        
                        {/* Step 4: Assessment Button */}
                        <Grid item xs={12}>
                            <Paper elevation={2} sx={{ p: 3, textAlign: 'center' }}>
                                <Button
                                    variant="contained"
                                    size="large"
                                    onClick={handleAssess}
                                    disabled={!torSegmentText.trim() || !methodologyText.trim() || isAssessing}
                                    startIcon={isAssessing ? <CircularProgress size={24} /> : <AssessmentIcon />}
                                    sx={{ minWidth: 200 }}
                                >
                                    {isAssessing ? 'Assessing...' : 'Assess Methodology'}
                                </Button>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                                    Compare methodology against ToR requirements and get detailed compliance assessment
                                </Typography>
                            </Paper>
                        </Grid>
                    </>
                )}
                
                {/* Step 5: Assessment Results */}
                {assessmentResult && (
                    <Grid item xs={12}>
                        <Paper elevation={3} sx={{ p: 3 }}>
                            <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
                                Assessment Results
                            </Typography>
                            <MethodologyAssessmentResults assessment={assessmentResult} />
                        </Paper>
                    </Grid>
                )}
            </Grid>
            
            {/* Reset Button */}
            {(torFile || torExtraction || assessmentResult) && (
                <Box sx={{ mt: 4, textAlign: 'center' }}>
                    <Button
                        variant="outlined"
                        onClick={handleReset}
                    >
                        Start Over
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default MethodologyAssessmentPage;

